import {
  createTheme,
  rem,
  type CSSVariablesResolver,
  type MantineColorsTuple,
} from '@mantine/core';
import {
  INK,
  BRASS,
  NEUTRAL,
  STATUS,
  GREEN_SCALE,
  YELLOW_SCALE,
  RED_SCALE,
  BLUE_SCALE,
  WHITE,
  FONT_BODY,
  FONT_DISPLAY,
  FONT_ACCENT,
  FONT_MONO,
  FONT_ACCENT_SCALE,
  BREAKPOINTS_PX,
} from '@repo/shared/brand';

/* ─────────────────────────────────────────────────────────────────────────
 * VINTAGE — Design System v1.0 — Mantine theme
 *
 * A design language for community banks & credit unions: the trust, gravitas,
 * and exactness of traditional finance (ink-teal, engraved certificates, ledger
 * paper, brass fittings, serif numerals on a passbook) executed with the
 * clarity, spacing, and responsiveness of modern SaaS.
 *
 * Brand axes
 *   • Ink (teal)   — the bedrock trust colour: a deep desaturated petrol/teal
 *                    (#193C47). Primary actions, links, focus.
 *   • Gilt (brass) — antique gold accent, used sparingly like gold leaf:
 *                    premium/featured emphasis, the heading <em> accent (set in
 *                    the decorative face), the optional "accent" button. Teal +
 *                    antique gold is the heritage pairing. Never fill-everything.
 *   • Paper        — warm, near-neutral document tones; crisp hairline rules do
 *                    the structural work, shadows stay restrained and ink-tinted.
 *   • Ledger semantics — credit green / debit red / amber / steel-blue info.
 *
 * The visual language lives in two layers that resolve to the SAME values:
 *   1. Mantine scales (colors/spacing/radius/shadows) — used by built-in
 *      components and the `color="…"` / `radius="…"` props.
 *   2. cssVariablesResolver below — the design-system named tokens
 *      (--color-primary, --bg-surface, --tint-gold-bg, --fz-h1, --sp-md, …)
 *      every UIKit CSS Module consumes. Token NAMES are stable across re-skins;
 *      only their values change here.
 *
 * Every text/fill pair below was WCAG-checked (AA+ in light and dark).
 * ───────────────────────────────────────────────────────────────────────── */

/* ── Palette ──
 * The raw scales live in `@repo/shared/brand` so the marketing website renders
 * the identical brand (see that module's header). This file owns everything
 * downstream of them: the Mantine wiring and the semantic token layer.
 *
 * The `satisfies` re-assertions are load-bearing — `@repo/shared/brand` is
 * framework-free and types its scales as a plain 10-tuple, so these are what
 * actually prove the shared shape still matches what Mantine requires. A scale
 * that gained or lost a shade upstream fails HERE, at this app's typecheck,
 * rather than rendering a broken ramp.
 *
 * Shade landmarks worth knowing when reading the tokens below: ink-7 (#193C47)
 * is the primary solid fill and carries white text at 11.8:1 (AAA); Mantine
 * derives hover from ink-6 (9.0:1) and active from ink-8, and the named
 * --button-primary-bg tokens pin those exact values so design-system variants
 * match the derivation. Brass 3–4 are gold-leaf fills that take DARK ink text,
 * never white; brass-6 is the text-on-light tier (5.3:1). */
const ink = INK satisfies MantineColorsTuple;
const brass = BRASS satisfies MantineColorsTuple;
const neutral = NEUTRAL satisfies MantineColorsTuple;

/* Mantine status tuples — the ledger ramps, so `<Badge color="green">` /
 * `color="red"` etc. render with the design's exact hues. */
const greenTuple = GREEN_SCALE satisfies MantineColorsTuple;
const yellowTuple = YELLOW_SCALE satisfies MantineColorsTuple;
const redTuple = RED_SCALE satisfies MantineColorsTuple;
const blueTuple = BLUE_SCALE satisfies MantineColorsTuple;

/* Re-exported for consumers that import the type stack + breakpoints from the
 * theme (its historical home) rather than reaching for the brand module. */
export { FONT_DISPLAY, FONT_ACCENT, FONT_MONO, BREAKPOINTS_PX };

// Derived, not hard-coded: the px value now lives in `@repo/shared/brand`, so a
// literal '80em' here could silently disagree with it after an upstream change.
const DESKTOP_EM = `${BREAKPOINTS_PX.desktop / 16}em`; // 80em
const NONE_EM = '0em';

export const theme = createTheme({
  primaryColor: 'ink',
  // primaryShade 7 (=#193C47 ink-700) gives white-on-fill 11.8:1 (AAA). The
  // design-system Button overrides bg via tokens; this governs other brand
  // surfaces (checked Checkbox/Radio/Switch, Pagination active, Tabs underline).
  primaryShade: { light: 7, dark: 6 },
  // Clickable form controls (Checkbox/Radio/Switch and their labels) get the
  // pointer cursor app-wide — they read as interactive. Mantine defaults this to
  // `default`; `pointer` matches the rest of the kit's clickable affordances.
  cursorType: 'pointer',
  // Honor the OS "reduce motion" setting for Mantine's own component
  // transitions (Modal/Drawer/Menu/etc.). Our custom keyframes + transitions
  // are collapsed by the global guard in styles/motion.css; this covers the
  // Mantine-driven ones the guard can't reach as cleanly.
  respectReducedMotion: true,
  colors: {
    ink: [...ink] as unknown as MantineColorsTuple,
    brass: [...brass] as unknown as MantineColorsTuple,
    // `neutral` is also exposed under Mantine's `gray` key — Mantine internals
    // (Skeleton gradient, Code/Kbd/Mark backgrounds, default Divider, etc.)
    // read `--mantine-color-gray-*` directly, so overriding it propagates warm
    // paper neutrals everywhere without per-component overrides.
    neutral: [...neutral] as unknown as MantineColorsTuple,
    gray: [...neutral] as unknown as MantineColorsTuple,
    // Status tuples re-shaded so `<Badge color="green">` / `color="red"` etc.
    // render with the design's exact ledger hues.
    green: [...greenTuple] as unknown as MantineColorsTuple,
    yellow: [...yellowTuple] as unknown as MantineColorsTuple,
    red: [...redTuple] as unknown as MantineColorsTuple,
    blue: [...blueTuple] as unknown as MantineColorsTuple,
  },
  fontFamily: FONT_BODY,
  fontFamilyMonospace: FONT_MONO,
  // Headings render in Libre Baskerville (the heritage voice) at 700 — bold
  // Baskerville anchors the display register with authority. The kit `Heading`
  // primitive keeps level 3/4 on the sans face for dense working titles; this
  // governs raw Mantine <Title> + internal heading elements (Modal/Drawer title).
  headings: {
    fontFamily: FONT_DISPLAY,
    fontWeight: '700',
    sizes: {
      h1: {
        fontSize: 'calc(2.5rem * var(--mantine-scale))', // 40px
        lineHeight: '1.1',
        fontWeight: '700',
      },
      h2: {
        fontSize: 'calc(1.875rem * var(--mantine-scale))', // 30px
        lineHeight: '1.2',
        fontWeight: '700',
      },
      h3: {
        // Kept in sync with the kit Heading level={3} (typography.module.css
        // .h3) — 18px card-title scale. The two must not drift.
        fontSize: 'calc(1.125rem * var(--mantine-scale))',
        lineHeight: '1.3',
        fontWeight: '600',
      },
      h4: { fontSize: 'calc(1.0625rem * var(--mantine-scale))', lineHeight: '1.35', fontWeight: '600' },
      h5: { fontSize: 'calc(0.9375rem * var(--mantine-scale))', lineHeight: '1.4', fontWeight: '600' },
      h6: { fontSize: 'calc(0.8125rem * var(--mantine-scale))', lineHeight: '1.45', fontWeight: '600' },
    },
  },
  breakpoints: {
    // Two-tier system. xs/sm/md collapse to "always-true" (=mobile), lg/xl gate
    // the desktop layout. Existing `visibleFrom="sm"|"md"` props become no-ops.
    xs: NONE_EM,
    sm: NONE_EM,
    md: NONE_EM,
    lg: DESKTOP_EM,
    xl: DESKTOP_EM,
  },
  spacing: {
    xs: '0.5rem', // 8px  (design sp-xs)
    sm: '0.75rem', // 12px (design sp-sm)
    md: '1rem', // 16px (design sp-md)
    lg: '1.5rem', // 24px (design sp-lg)
    xl: '2rem', // 32px (design sp-xl)
  },
  radius: {
    // Crisper, more precise corners than a consumer SaaS — finance reads as
    // exact, not bubbly. Buttons/inputs sit at md (6px); cards/modals at lg (8px).
    xs: '0.125rem', // 2px
    sm: '0.25rem', // 4px
    md: '0.375rem', // 6px  — buttons / inputs / chips
    lg: '0.5rem', // 8px  — cards / modals
    xl: '0.75rem', // 12px — large panels
  },
  shadows: {
    // Ink-tinted (rgba 12,30,36) instead of pure black — depth that reads
    // branded and refined. Restrained spread; hairline borders carry structure.
    xs: '0 1px 2px rgba(12,30,36,0.05), 0 1px 1px rgba(12,30,36,0.03)',
    sm: '0 2px 8px rgba(12,30,36,0.07), 0 1px 3px rgba(12,30,36,0.05)',
    md: '0 12px 28px rgba(12,30,36,0.10), 0 4px 10px rgba(12,30,36,0.06)',
    lg: '0 24px 56px rgba(12,30,36,0.14), 0 8px 20px rgba(12,30,36,0.08)',
    xl: '0 32px 72px rgba(12,30,36,0.18), 0 12px 28px rgba(12,30,36,0.10)',
  },
  defaultRadius: 'md',
  components: {
    Button: { defaultProps: { radius: 'md' } },
    TextInput: { defaultProps: { radius: 'md' } },
    PasswordInput: { defaultProps: { radius: 'md' } },
    Paper: { defaultProps: { radius: 'lg' } },
    ActionIcon: { defaultProps: { radius: 'md' } },
    Modal: { defaultProps: { radius: 'lg' } },
    Switch: { defaultProps: { withThumbIndicator: false } },
  },
});

/* ─────────────────────────────────────────────────────────────────────────
 * CSS Variables — design-system named tokens
 *
 * `variables`     — mode-independent (spacing, radius, type scale, motion, layout)
 * `light` / `dark` — theme-dependent, emitted under Mantine's color-scheme attr.
 * ───────────────────────────────────────────────────────────────────────── */

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    /* ── App layout ── */
    '--app-sidebar-width': '15rem', // 240px
    '--app-header-height': '4rem', // 64px
    '--app-font-display': FONT_DISPLAY,

    /* ── Layout constants ── */
    '--max-content': '950px',
    '--max-reading': '680px',
    '--sidebar-w': '15rem',
    // Icons-only rail width when the sidebar is collapsed (≥1280px desktop only).
    // Sized to seat the 18px nav icon AND the small below-icon page caption on one
    // line (the longest label, "Portfolio", at 10px/600) without truncation — a
    // touch wider than an icon-only rail by design.
    '--sidebar-w-collapsed': '5rem', // 80px
    '--topbar-h': '4rem',

    /* ── Spacing ── */
    '--sp-0': '0',
    '--sp-2xs': '0.25rem', // 4px
    '--sp-xs': '0.5rem', // 8px
    '--sp-sm': '0.75rem', // 12px
    '--sp-md': '1rem', // 16px
    '--sp-lg': '1.5rem', // 24px
    '--sp-xl': '2rem', // 32px
    '--sp-2xl': '3rem', // 48px
    '--sp-3xl': '4rem', // 64px
    '--sp-4xl': '6rem', // 96px
    '--sp-5xl': '8rem', // 128px

    /* ── Control height — one scale shared by buttons AND inputs/selects so the
       two never drift. Consumed by `--button-height-*` (Button.module.css) and
       by `--input-height-*` (mantine-overrides.css). ── */
    '--control-height-xs': rem(28),
    '--control-height-sm': rem(32), // app default
    '--control-height-md': rem(38),
    '--control-height-lg': rem(44),
    '--control-height-xl': rem(52),

    /* ── Radius ── */
    '--radius-xs': '0.125rem', // 2px
    '--radius-sm': '0.25rem', // 4px
    '--radius-md': '0.375rem', // 6px
    '--radius-lg': '0.5rem', // 8px
    '--radius-xl': '0.75rem', // 12px
    '--radius-2xl': '1rem', // 16px
    '--radius-pill': '6.25rem', // 100px

    /* ── Type scale — the single source of truth for font sizes. The kit
       typography primitives + headings consume these so the scale is auditable
       in one place (and documented on /style-guide). ── */
    '--fz-display': '3.5rem', // 56px — serif hero / greeting
    '--fz-h1': '2.5rem', // 40px — serif
    '--fz-h2': '1.875rem', // 30px — serif
    '--fz-h3': '1.125rem', // 18px — sans (card titles)
    '--fz-h4': '1.0625rem', // 17px — sans
    '--fz-body': '0.9375rem', // 15px
    '--fz-body-sm': '0.8125rem', // 13px
    '--fz-label': '0.75rem', // 12px — uppercase eyebrow
    '--fz-caption': '0.6875rem', // 11px
    '--fz-stat': '1.75rem', // 28px — mono figures
    '--fz-stat-lg': '2.75rem', // 44px
    '--fz-mono': '0.78125rem', // 12.5px
    '--lh-tight': '1.1',
    '--lh-snug': '1.3',
    '--lh-normal': '1.5',
    '--lh-relaxed': '1.6',

    /* ── Motion ── */
    '--ease-out': 'cubic-bezier(0.22, 0.61, 0.36, 1)',
    '--ease-inout': 'cubic-bezier(0.65, 0, 0.35, 1)',
    '--ease-spring': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    '--dur-instant': '80ms',
    '--dur-fast': '140ms',
    '--dur-base': '220ms',
    '--dur-slow': '360ms',
    '--dur-page': '480ms',
    '--transition': '220ms cubic-bezier(0.22, 0.61, 0.36, 1)',

    /* ── Overlay blur (modal/drawer scrim) ── */
    '--blur-overlay': '2px',

    /* ── Font stacks (mirror Mantine's vars by design-system names) ── */
    '--font-display': FONT_DISPLAY,
    '--font-sans': FONT_BODY,
    '--font-mono': FONT_MONO,
    // Decorative accent (Cormorant Garamond) — the heading <em> flourish, used
    // sparingly (usually italic + the gilt accent colour). See typography.module.css.
    '--font-accent': FONT_ACCENT,
    // Optical-size compensation for the accent face. Cormorant Garamond has a
    // smaller x-/cap-height than Libre Baskerville and Montserrat, so at an equal
    // `font-size` it reads visibly smaller. Bump accent text by this factor so a
    // Cormorant word set inline (a single word / part of a sentence) matches the
    // height of the surrounding copy. The kit `<em>` accent (.display/.heading em)
    // applies it automatically; reach for it directly — `calc(1em *
    // var(--font-accent-scale))` — whenever you set --font-accent by hand inline.
    // Calibrated against Libre Baskerville; see the frontend-ui skill.
    '--font-accent-scale': String(FONT_ACCENT_SCALE),
  },

  light: {
    /* ── Brand — Ink ── */
    '--color-primary': ink[6], // #234E5C — accent (links, icons, dots) 9.0:1
    '--color-primary-light': ink[4], // #4C7E8E
    '--color-primary-pale': ink[2], // #A8C7D0
    '--color-primary-deep': ink[8], // #122E37
    '--color-secondary': brass[4], // #C49A3A — secondary accent = brass
    '--color-secondary-light': brass[3], // #D9B968

    /* ── Accent — Gilt (brass) ── */
    '--color-accent': brass[4], // #C49A3A — gold-leaf fill / borders (dark text)
    '--color-accent-text': brass[6], // #896619 — brass text/icon on light (5.3:1)
    '--color-accent-deep': brass[6],
    '--accent-em': brass[6], // heading <em> gold-leaf accent

    /* Solid-fill brand (WCAG-passing) — primary buttons & pills carry white text */
    '--button-primary-bg': ink[7], // #193C47
    '--button-primary-bg-hover': ink[6], // #234E5C
    '--button-primary-bg-active': ink[8], // #122E37
    '--fg-on-primary': WHITE,

    /* Solid-fill accent — the optional brass "accent" button carries DARK text */
    '--button-accent-bg': brass[4], // #C49A3A
    '--button-accent-bg-hover': brass[3], // #D9B968
    '--button-accent-bg-active': brass[5], // #A87F29
    '--fg-on-accent': ink[9], // #0C2128 — dark ink on gold (6.4:1)

    /* ── Semantic ── */
    '--color-success': STATUS.green400,
    '--color-success-text': STATUS.green600,
    '--color-warning': STATUS.amber400,
    '--color-warning-text': STATUS.amber600,
    '--color-error': STATUS.red400,
    '--color-error-text': STATUS.red600,
    '--color-info': STATUS.blue400,
    '--color-info-text': STATUS.blue600,

    /* ── Surfaces (depth-ordered) ── */
    '--bg-primary': neutral[0], // #F7F5F1 — page (warm paper)
    '--bg-surface': WHITE, // #FFFFFF — card
    '--bg-raised': neutral[1], // #EFEDE7 — well / tab bar bg
    '--bg-sunken': neutral[2], // #E3E0D8 — input fill, code
    '--bg-overlay': 'rgba(10,21,25,0.52)', // ink scrim
    '--bg-nav': 'rgba(255,255,255,0.85)', // blur-through nav

    /* ── Foregrounds ── */
    '--fg-primary': neutral[8], // #28261E — headings ink
    '--fg-secondary': neutral[7], // #3A372E — body
    '--fg-tertiary': neutral[5], // #726E62 — muted (5.1:1)
    '--fg-disabled': neutral[4], // #AEA99D
    '--fg-inverse': WHITE,
    '--fg-brand': ink[6],
    '--fg-link': ink[6],

    /* ── Borders — warm "printed rule" hairlines (ink-warm, not pure black) ── */
    '--border-subtle': 'rgba(40,38,30,0.05)',
    '--border-default': 'rgba(40,38,30,0.11)',
    '--border-strong': 'rgba(40,38,30,0.17)',
    '--border-focus': ink[5], // #2F6172
    '--border-hover-brand': 'rgba(35,78,92,0.32)', // interactive-surface hover edge

    /* ── Interactive ── */
    '--hover-bg': 'rgba(40,38,30,0.04)',
    '--active-bg': 'rgba(40,38,30,0.07)',
    '--selected-bg': 'rgba(35,78,92,0.08)',
    '--selected-fg': ink[6],
    '--focus-ring': '0 0 0 3px rgba(47,97,114,0.30)',

    /* ── Disabled — one consistent treatment across all controls (flat muted
       neutral fill, low-contrast text, never mistaken for an active control) ── */
    '--disabled-bg': neutral[2], // #E3E0D8
    '--disabled-border': 'rgba(40,38,30,0.10)',
    '--disabled-fg': neutral[4], // #AEA99D

    /* ── Badge / tint pairs (bg + fg per tone) ── */
    '--tint-brand-bg': 'rgba(35,78,92,0.10)',
    '--tint-brand-fg': ink[6],
    '--tint-gold-bg': 'rgba(196,154,58,0.16)',
    '--tint-gold-fg': brass[6],
    '--tint-green-bg': 'rgba(46,148,96,0.12)',
    '--tint-green-fg': STATUS.green600,
    '--tint-neutral-bg': 'rgba(40,38,30,0.06)',
    '--tint-neutral-fg': neutral[6],
    '--tint-warning-bg': 'rgba(206,150,34,0.16)',
    '--tint-warning-fg': STATUS.amber600,
    '--tint-error-bg': 'rgba(200,70,59,0.12)',
    '--tint-error-fg': STATUS.red600,
    '--tint-info-bg': 'rgba(45,128,194,0.12)',
    '--tint-info-fg': STATUS.blue600,
    /* Stronger info tint pair — an emphasized fill (icon tiles, progress tracks)
       and a matching border, for surfaces that sit ON the info wash. */
    '--tint-info-bg-strong': 'rgba(45,128,194,0.18)',
    '--tint-info-border': 'rgba(45,128,194,0.25)',

    /* ── Elevation (mirror Mantine shadows by design-system name) ── */
    '--shadow-0': 'none',
    '--shadow-1': '0 1px 2px rgba(12,30,36,0.05), 0 1px 1px rgba(12,30,36,0.03)',
    '--shadow-2': '0 2px 8px rgba(12,30,36,0.07), 0 1px 3px rgba(12,30,36,0.05)',
    '--shadow-3': '0 12px 28px rgba(12,30,36,0.10), 0 4px 10px rgba(12,30,36,0.06)',
    '--shadow-4': '0 24px 56px rgba(12,30,36,0.14), 0 8px 20px rgba(12,30,36,0.08)',
    /* Card resting/hover elevation — tight, soft, weighted downward (paper-on-
       desk). Deliberately smaller than --shadow-2 so cards sit, not float. */
    '--shadow-card': '0 1px 2px rgba(12,30,36,0.05), 0 1px 3px rgba(12,30,36,0.04)',
    '--shadow-card-hover': '0 2px 4px rgba(12,30,36,0.06), 0 6px 14px rgba(12,30,36,0.08)',
    '--shadow-focus': '0 0 0 3px rgba(47,97,114,0.30)',

    /* ── Skeleton placeholders — deliberately faint (a hint, not a block) ── */
    '--skeleton-base': 'rgba(40,38,30,0.05)',
    '--skeleton-sheen': 'rgba(40,38,30,0.09)',

    /* ── Legacy app-* names retained for in-flight consumers ── */
    '--app-surface-raised': WHITE,
    '--app-surface-sunken': neutral[1],
    '--app-border-color': 'rgba(40,38,30,0.11)',
    '--app-text-muted': neutral[6],
    '--app-hero-bg': ink[9],
    '--app-hero-text': neutral[0],
  },

  dark: {
    /* ── Brand — Ink (lighter shades read on dark surfaces) ── */
    '--color-primary': ink[3], // #79A4B1
    '--color-primary-light': ink[2], // #A8C7D0
    '--color-primary-pale': ink[1], // #D2E3E8
    '--color-primary-deep': ink[5], // #2F6172
    '--color-secondary': brass[3], // #D9B968
    '--color-secondary-light': brass[2], // #E9D49B

    /* ── Accent — Gilt ── */
    '--color-accent': brass[4],
    '--color-accent-text': brass[3], // #D9B968 — brass on dark (9.8:1)
    '--color-accent-deep': brass[3],
    '--accent-em': brass[3],

    /* Brand fill — brighter teal so the button reads against the dark page */
    '--button-primary-bg': ink[6], // #234E5C (white text 9.0:1)
    '--button-primary-bg-hover': ink[5], // #2F6172
    '--button-primary-bg-active': ink[7], // #193C47
    '--fg-on-primary': WHITE,

    '--button-accent-bg': brass[4],
    '--button-accent-bg-hover': brass[3],
    '--button-accent-bg-active': brass[5],
    '--fg-on-accent': ink[9],

    /* ── Semantic — lighter shades for dark-mode legibility ── */
    '--color-success': STATUS.green300,
    '--color-success-text': STATUS.green300,
    '--color-warning': STATUS.amber300,
    '--color-warning-text': STATUS.amber300,
    '--color-error': STATUS.red300,
    '--color-error-text': STATUS.red300,
    '--color-info': STATUS.blue300,
    '--color-info-text': STATUS.blue300,

    /* ── Surfaces — deep teal-charcoal (after-hours ledger) ── */
    '--bg-primary': '#0E1519', // page
    '--bg-surface': '#141C21', // card
    '--bg-raised': '#1C262C', // well / tab bar
    '--bg-sunken': '#090F12', // input fill, code
    '--bg-overlay': 'rgba(4,8,10,0.66)',
    '--bg-nav': 'rgba(14,21,25,0.92)',

    /* ── Foregrounds ── */
    '--fg-primary': '#ECEAE3', // warm paper-white
    '--fg-secondary': 'rgba(236,234,227,0.74)',
    '--fg-tertiary': 'rgba(236,234,227,0.42)',
    '--fg-disabled': 'rgba(236,234,227,0.26)',
    '--fg-inverse': '#0E1519',
    '--fg-brand': ink[3],
    '--fg-link': ink[3],

    /* ── Borders ── */
    '--border-subtle': 'rgba(255,255,255,0.05)',
    '--border-default': 'rgba(255,255,255,0.10)',
    '--border-strong': 'rgba(255,255,255,0.16)',
    '--border-focus': ink[4], // #4C7E8E
    '--border-hover-brand': 'rgba(121,164,177,0.34)',

    /* ── Interactive ── */
    '--hover-bg': 'rgba(255,255,255,0.05)',
    '--active-bg': 'rgba(255,255,255,0.08)',
    '--selected-bg': 'rgba(76,126,142,0.16)',
    '--selected-fg': ink[3],
    '--focus-ring': '0 0 0 3px rgba(76,126,142,0.40)',

    /* ── Disabled ── */
    '--disabled-bg': 'rgba(255,255,255,0.06)',
    '--disabled-border': 'rgba(255,255,255,0.08)',
    '--disabled-fg': 'rgba(236,234,227,0.30)',

    /* ── Badge / tint pairs ── */
    '--tint-brand-bg': 'rgba(76,126,142,0.18)',
    '--tint-brand-fg': ink[3],
    '--tint-gold-bg': 'rgba(196,154,58,0.20)',
    '--tint-gold-fg': brass[3],
    '--tint-green-bg': 'rgba(46,148,96,0.20)',
    '--tint-green-fg': STATUS.green300,
    '--tint-neutral-bg': 'rgba(255,255,255,0.08)',
    '--tint-neutral-fg': 'rgba(236,234,227,0.60)',
    '--tint-warning-bg': 'rgba(206,150,34,0.20)',
    '--tint-warning-fg': STATUS.amber300,
    '--tint-error-bg': 'rgba(200,70,59,0.20)',
    '--tint-error-fg': STATUS.red300,
    '--tint-info-bg': 'rgba(45,128,194,0.20)',
    '--tint-info-fg': STATUS.blue300,
    '--tint-info-bg-strong': 'rgba(45,128,194,0.32)',
    '--tint-info-border': 'rgba(91,160,214,0.35)',

    /* ── Elevation — heavier shadows for dark surfaces ── */
    '--shadow-0': 'none',
    '--shadow-1': '0 1px 3px rgba(0,0,0,0.34)',
    '--shadow-2': '0 4px 16px rgba(0,0,0,0.44)',
    '--shadow-3': '0 12px 40px rgba(0,0,0,0.54)',
    '--shadow-4': '0 24px 64px rgba(0,0,0,0.64)',
    '--shadow-card': '0 1px 2px rgba(0,0,0,0.34), 0 2px 6px rgba(0,0,0,0.34)',
    '--shadow-card-hover': '0 2px 4px rgba(0,0,0,0.40), 0 6px 14px rgba(0,0,0,0.40)',
    '--shadow-focus': '0 0 0 3px rgba(76,126,142,0.40)',

    /* ── Skeleton placeholders — faint on dark surfaces ── */
    '--skeleton-base': 'rgba(255,255,255,0.05)',
    '--skeleton-sheen': 'rgba(255,255,255,0.09)',

    /* ── Legacy app-* names ── */
    '--app-surface-raised': '#141C21',
    '--app-surface-sunken': '#090F12',
    '--app-border-color': 'rgba(255,255,255,0.10)',
    '--app-text-muted': 'rgba(236,234,227,0.60)',
    '--app-hero-bg': '#0E1519',
    '--app-hero-text': '#ECEAE3',
  },
});
